import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import Tree from './Tree'
import ConfirmModal from 'pages/parts/ConfirmModal'
import DeleteModal from 'pages/parts/DeleteModal'

import { httpOk, GET, POST, PUT, DELETE } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import HintIcon from 'pages/parts/HintIcon'

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const headerThStyle = {
	border: "none",
	boxShadow: "none",
	background: "var(--dark-surface, #fff)",
	fontSize: "13px",
}

function Index() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const reduxAccount = useSelector(state => state.account)
	const posId = reduxAccount.pos_id

	const [accounts, setAccounts] = useState([])
	const [dataDuplicate, setDataDuplicate] = useState([])
	const [showHidden, setShowHidden] = useState(false)
	const [selectedItem, setSelectedItem] = useState({})
	const [showModal, setShowModal] = useState(false)
	const [showConfirm, setShowConfirm] = useState(false)
	// какое значение применить ко всем счетам: false = ограничить, true = снять ограничение
	const [bulkAllowNegative, setBulkAllowNegative] = useState(false)
	const [showCorrection, setShowCorrection] = useState(false)
	const [correction, setCorrection] = useState({})
	const [showDelete, setShowDelete] = useState(false)
	const [deleteAccount, setDeleteAccount] = useState({})

	async function createUpdate(e) {
		e.preventDefault()

		var response;
		if (selectedItem.id) {
			// Скрытие шлём, только если его поменяли: у скрытого счёта, на который потом пришли деньги,
			// сервер откажет «есть остаток» — и переименовать его было бы нельзя
			const payload = { ...selectedItem }
			if (payload.is_hidden === payload.was_hidden) delete payload.is_hidden
			delete payload.was_hidden
			response = await PUT('/finance/accounts', payload, { loader: true })
		} else {
			response = await POST('/finance/accounts', selectedItem, { loader: true })
		}

		if (httpOk(response)) {
			getData()
			toggleModal(false)
		}
	}

	function toggleModal(bool = false, item = {}) {
		setSelectedItem(item)
		setShowModal(bool)
	}

	// Открыть подтверждение массового ограничения/снятия по всем счетам
	function askBulkRestrict(allowNegative) {
		setBulkAllowNegative(allowNegative)
		setShowConfirm(true)
	}

	async function restrictAll() {
		const response = await POST('/finance/accounts/restrict-all', { allow_negative: bulkAllowNegative }, { loader: true })
		if (httpOk(response)) {
			setShowConfirm(false)
			getData()
		}
	}

	function askDelete(item) {
		const group = accounts.find(account => account.id === item.parent_id)
		setDeleteAccount({ id: item.id, name: item.name, is_main_leaf: item.is_main_leaf, group_name: group?.name })
		setShowDelete(true)
	}

	// Сервер удаляет только пустой счёт; если мешают проводки или ссылки — вернёт 400 с причиной (тост)
	async function deleteItem() {
		const response = await DELETE(`/finance/accounts/${deleteAccount.id}`, {}, { loader: true })
		setShowDelete(false)
		if (httpOk(response)) {
			getData()
		}
	}

	// Открыть корректировку остатка конечного счёта (по умолчанию до 0)
	function openCorrection(item) {
		setCorrection({
			account_id: item.id,
			name: item.name,
			current: item.balance,
			target_balance: 0,
			offset_account_id: null,
			note: '',
		})
		setShowCorrection(true)
	}

	async function submitCorrection(e) {
		e.preventDefault()
		if (!correction.account_id || !correction.offset_account_id) return

		const response = await POST('/finance/accounts/correct-balance', {
			pos_id: posId,
			account_id: correction.account_id,
			target_balance: correction.target_balance === '' || correction.target_balance == null ? 0 : correction.target_balance,
			offset_account_id: correction.offset_account_id,
			note: correction.note || null,
		}, { loader: true })

		if (httpOk(response)) {
			setShowCorrection(false)
			getData()
		}
	}

	function makeTree(list) {
		var map = {}
		var node = []
		var roots = []
		var i;

		for (i = 0; i < list.length; i += 1) {
			map[list[i]['id']] = i
			list[i]['children'] = []
		}

		for (i = 0; i < list.length; i += 1) {
			node = list[i];
			if (node.parent_id) {
				if (map[node.parent_id] !== undefined) {
					list[map[node.parent_id]]['children'].push(node);
				}
			} else {
				roots.push(node);
			}
		}
		return roots;
	}

	function selectItem(item) {
		var elements = document.getElementsByClassName(`child-${item.id}`);
		var selectedElements = document.getElementsByClassName('step-selected');

		for (let i = 0; i < selectedElements.length; i++) {
			selectedElements[i]['classList'].remove('step-selected');
		}

		if (elements[0]) elements[0]['classList'].add('step-selected')
	}

	function countNodes(nodes = []) {
		let total = 0
		nodes.forEach((item) => {
			total += 1
			if (item.children?.length) total += countNodes(item.children)
		})
		return total
	}

	// Плоский список -> дерево для Select. Родителей тоже можно выбирать (счёт создаётся под ними).
	function buildCategoryOptions(accounts = []) {
		const childrenOf = {}
		accounts.forEach((item) => {
			const parentId = item.parent_id == null ? null : Number(item.parent_id)
			if (!childrenOf[parentId]) childrenOf[parentId] = []
			childrenOf[parentId].push(item)
		})

		const options = [{ id: null, name: t('main'), treeLabel: t('main'), level: 0 }]
		const walk = (parentId, level) => {
			(childrenOf[parentId] || []).forEach((item) => {
				options.push({
					...item,
					level,
					treeLabel: `${'\u00A0\u00A0\u00A0\u00A0'.repeat(level)}${item.name}${item.code ? ` (${item.code})` : ''}`,
				})
				walk(Number(item.id), level + 1)
			})
		}
		walk(null, 0)
		return options
	}

	// Куда можно перенести счёт: без «Основного» уровня, самого счёта и его подсчетов (иначе цикл).
	// Остальные ограничения (ветка, операции по счёту) проверяет сервер и отвечает причиной.
	function moveOptions(accountId) {
		const excluded = new Set([accountId])
		let grew = true
		while (grew) {
			grew = false
			for (const option of dataDuplicate) {
				if (option.id != null && !excluded.has(option.id) && excluded.has(option.parent_id)) {
					excluded.add(option.id)
					grew = true
				}
			}
		}
		return dataDuplicate.filter(option => option.id != null && !excluded.has(option.id))
	}

	// Скрытые счета вместе с подсчетами (скрывают ветку целиком)
	function hiddenIds(list) {
		const hidden = new Set(list.filter(item => item.is_hidden).map(item => item.id))
		let grew = true
		while (grew) {
			grew = false
			for (const item of list) {
				if (!hidden.has(item.id) && hidden.has(item.parent_id)) {
					hidden.add(item.id)
					grew = true
				}
			}
		}
		return hidden
	}

	// Что показать в дереве: скрытые — только по «Показать скрытые». Скрытый счёт, на который
	// пришли деньги, показываем всегда (деньги не должны пропасть из виду) вместе с его родителями
	function visibleAccounts(list) {
		if (showHidden) return list
		const hidden = hiddenIds(list)
		const byId = {}
		list.forEach(item => { byId[item.id] = item })
		const visible = new Set()
		list.forEach(item => {
			const hasMoney = item.balance != null && item.balance !== '' && Number(item.balance) !== 0
			if (hidden.has(item.id) && !hasMoney) return
			let current = item
			while (current && !visible.has(current.id)) {
				visible.add(current.id)
				current = byId[current.parent_id]
			}
		})
		return list.filter(item => visible.has(item.id))
	}

	async function getData() {
		const response = await GET(`/finance/accounts?pos_id=${posId}`, {}, { loader: true })

		if (httpOk(response)) {
			setAccounts(response.data)
			// Группу для нового счёта и переноса выбираем только среди нескрытых
			const hidden = hiddenIds(response.data)
			setDataDuplicate(buildCategoryOptions(response.data.filter(item => !hidden.has(item.id))))
		}
	}

	useEffect(() => {
		if (posId) getData()
	}, [posId]) // eslint-disable-line react-hooks/exhaustive-deps

	// «Основной» подсчёт удаляется только последним в группе (тогда группа схлопывается) —
	// пока рядом есть другие подсчета, корзину не показываем: сервер всё равно откажет
	function isDeleteLocked(item) {
		return item.is_main_leaf && accounts.some(account => account.parent_id === item.parent_id && account.id !== item.id)
	}

	// makeTree дописывает children в узлы — строим дерево на копиях
	const data = makeTree(visibleAccounts(accounts).map(item => ({ ...item, selected: false, show: true, delete_locked: isDeleteLocked(item) })))
	const totalNodes = countNodes(data)
	const hiddenCount = accounts.filter(item => item.is_hidden).length

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('categories')} [{totalNodes}]
				</h4>
				<div className="d-flex gap-2">
					{hiddenCount > 0 &&
						<button className={`btn-rounded btn ${showHidden ? 'btn-secondary' : 'btn-outline-secondary'}`}
							onClick={() => setShowHidden(!showHidden)}
							title={t('show_hidden_accounts')}>
							<i className={`uil ${showHidden ? 'uil-eye' : 'uil-eye-slash'} me-1`}></i>
							{t('show_hidden_accounts')} ({hiddenCount})
						</button>
					}
					<button className="btn-rounded btn btn-outline-danger"
						onClick={() => askBulkRestrict(false)}
						title={t('restrict_all_accounts')}>
						<i className="uil uil-lock me-1"></i>
						{t('restrict_all_accounts')}
					</button>
					<button className="btn-rounded btn btn-outline-success"
						onClick={() => askBulkRestrict(true)}
						title={t('allow_all_accounts')}>
						<i className="uil uil-lock-open-alt me-1"></i>
						{t('allow_all_accounts')}
					</button>
					<button className="btn-rounded btn btn-outline-primary"
						onClick={() => getData()}
						title={t('refresh')}>
						<i className="uil uil-refresh me-1"></i>
						{t('refresh')}
					</button>
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModal(true)}>
						{t('create')}
					</button>
				</div>
			</div>

			<div className="card" style={{ ...cardStyle, minHeight: 'calc(100vh - 160px)' }}>
				<div className="card-body py-2 px-3">
					<div className="table-responsive">
						<table className="table align-middle mb-0 finance-accounts-table" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
							<thead>
								<tr style={{ border: "none" }}>
									<th className="py-1 text-muted fw-semibold" style={{ ...headerThStyle, width: '32%', fontSize: '12px' }}>{t('name2')}</th>
									<th className="py-1 text-muted fw-semibold" style={{ ...headerThStyle, width: '10%', fontSize: '12px' }}>{t('code')}</th>
									<th className="py-1 text-muted fw-semibold" style={{ ...headerThStyle, width: '24%', fontSize: '12px' }}>{t('type')}</th>
									<th className="py-1 text-muted fw-semibold" style={{ ...headerThStyle, width: '16%', fontSize: '12px' }}>{t('counterparty')}</th>
									<th className="py-1 text-muted fw-semibold text-end" style={{ ...headerThStyle, width: '18%', fontSize: '12px' }}>{t('balance')}</th>
									<th className="py-1" style={{ ...headerThStyle, width: '40px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data.length === 0 ? (
									<tr>
										<td colSpan="6" className="text-muted text-center py-4">{t('list_empty')}</td>
									</tr>
								) : (
									<Tree children={data} selectItem={selectItem}
										openTransactions={(item) => navigate(`/finance/accounts/${item.id}/transactions`)}
										openCorrection={openCorrection}
										deleteItem={askDelete}
										editItem={(item) => toggleModal(true, { id: item.id, name: item.name, parent_id: item.parent_id, is_own: item.is_own, is_main_leaf: item.is_main_leaf, allow_negative: item.allow_negative, is_hidden: !!item.is_hidden, was_hidden: !!item.is_hidden, is_capital: item.is_capital, ...(item.is_capital ? { share_percent: item.share_percent ?? '', pos_id: posId } : {}) })} />
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* нижний отступ, чтобы карточка не прилипала к низу страницы */}
			<div style={{ height: '24px' }}></div>

			<Modal show={showModal} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => toggleModal()}>
				<Modal.Header closeButton>
					<Modal.Title>
						{!selectedItem.id ? t('create') : t('edit')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={createUpdate} autoComplete="off">
						<div className="form-group">
							<label>{t('name2')}</label>
							<input type="text" className="form-control" name="name"
								value={selectedItem.name ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'name': e.target.value })} />
						</div>
						{!selectedItem.id &&
							<div className="form-group">
								<label>{t('category')}</label>
								<Select
									options={dataDuplicate}
									value={dataDuplicate?.find(option => option.id === selectedItem.parent_id) || ''}
									onChange={(option) => setSelectedItem({ ...selectedItem, 'parent_id': option.id })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.treeLabel || option.name}
									getOptionValue={(option) => option.id}
									menuPosition="fixed"
									menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
									menuPlacement="auto"
									styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
								/>
							</div>
						}
						{/* Перенос своего счёта в другую группу; системные и «(основной)» не переносятся */}
						{selectedItem.id && selectedItem.is_own && !selectedItem.is_main_leaf &&
							<div className="form-group">
								<label>{t('category')}<HintIcon text={t('account_move_hint')} /></label>
								<Select
									options={moveOptions(selectedItem.id)}
									value={dataDuplicate?.find(option => option.id != null && option.id === selectedItem.parent_id) || ''}
									onChange={(option) => setSelectedItem({ ...selectedItem, 'parent_id': option.id })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.treeLabel || option.name}
									getOptionValue={(option) => option.id}
									menuPosition="fixed"
									menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
									menuPlacement="auto"
									styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
								/>
							</div>
						}
						{/* Партнёр: доля в чистой прибыли точки — по ней P&L считает положено / выдано / остаток */}
						{selectedItem.id && selectedItem.is_capital && selectedItem.parent_id &&
							<div className="form-group">
								<label>{t('profit_share')}, %<HintIcon text={t('profit_share_hint')} /></label>
								<input type="number" className="form-control" min="0" max="100" step="0.01"
									value={selectedItem.share_percent ?? ''}
									onChange={(e) => setSelectedItem({ ...selectedItem, 'share_percent': e.target.value })} />
							</div>
						}
						{selectedItem.id &&
							<div className="form-group">
								<div className="form-check form-switch">
									<input className="form-check-input" type="checkbox" role="switch"
										id="account-restrict-negative"
										checked={selectedItem.allow_negative === false}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'allow_negative': !e.target.checked })} />
									<label className="form-check-label" htmlFor="account-restrict-negative">
										{t('restrict_negative_balance')}
										<HintIcon text={t('restrict_negative_balance_hint')} />
									</label>
								</div>
							</div>
						}
						{/* Скрыть неиспользуемый счёт: разделы плана (корни) не скрываются */}
						{selectedItem.id && selectedItem.parent_id &&
							<div className="form-group">
								<div className="form-check form-switch">
									<input className="form-check-input" type="checkbox" role="switch"
										id="account-hidden"
										checked={!!selectedItem.is_hidden}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'is_hidden': e.target.checked })} />
									<label className="form-check-label" htmlFor="account-hidden">
										{t('hide_account')}
										<HintIcon text={t('hide_account_hint')} />
									</label>
								</div>
							</div>
						}
						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModal()}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100">
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>

			<ConfirmModal
				modalDelete={showConfirm}
				setModalDelete={setShowConfirm}
				deleteItem={restrictAll} />

			<DeleteModal
				modalDelete={showDelete}
				setModalDelete={setShowDelete}
				deleteItem={deleteItem}
				message={deleteAccount.is_main_leaf
					? t('delete_main_leaf_confirm', { name: deleteAccount.name, group: deleteAccount.group_name })
					: t('delete_account_confirm', { name: deleteAccount.name })} />

			{/* КОРРЕКТИРОВКА ОСТАТКА */}
			<Modal show={showCorrection} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => setShowCorrection(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('correct_balance')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={submitCorrection} autoComplete="off">
						<div className="d-flex justify-content-between mb-2">
							<span className="text-muted">{correction.name}</span>
							<span>{t('current_balance')}: <b className={Number(correction.current) < 0 ? 'text-danger' : ''}>
								{correction.current == null || correction.current === '' ? '—' : formatMoney(correction.current, 2)}
							</b></span>
						</div>
						<div className="form-group">
							<label>{t('new_balance')}</label>
							<input type="text" className="form-control" name="target_balance" autoFocus
								value={correction.target_balance === '' || correction.target_balance == null ? '' : formatMoney(correction.target_balance)}
								onChange={(e) => setCorrection({ ...correction, 'target_balance': e.target.value.replace(/[^0-9.-]/g, '') })} />
						</div>
						<div className="form-group">
							<label>{t('correction_account')}<span className="required-mark">*</span><HintIcon text={t('correction_account_hint')} /></label>
							<Select
								options={dataDuplicate.filter(o => o.id != null && !o.has_children && o.id !== correction.account_id)}
								value={dataDuplicate.find(o => o.id === correction.offset_account_id) || ''}
								onChange={(option) => setCorrection({ ...correction, 'offset_account_id': option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.treeLabel || option.name}
								getOptionValue={(option) => option.id}
								menuPosition="fixed"
								menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
								menuPlacement="auto"
								styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
							/>
						</div>
						<div className="form-group">
							<label>{t('note')}</label>
							<input type="text" className="form-control" name="note"
								value={correction.note || ''}
								onChange={(e) => setCorrection({ ...correction, 'note': e.target.value })} />
						</div>
						<div className="d-flex gap-2 w-100 mt-3">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => setShowCorrection(false)}>
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100"
								disabled={!correction.offset_account_id}>
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Index
